import { TranscriptElementLegacy } from '@src/types.ts/transcript';
import type {
  TimelinePositionPropsNotNull,
  TranscriptTextToken,
} from '../types';

/**
 * Type for transcript modifications
 */
export type TranscriptModification = {
  id: string; // ID of the transcript element being modified
  type: 'replaceText' | 'mute' | 'addPunctuation' | 'removePunctuation'; // Type of modification
  value: any; // New value (e.g., replacement text, boolean for mute)
};

/**
 * Transcript class that manages the original transcript and active transcript derivation
 */
export class Transcript {
  /**
   * The original, immutable transcript elements
   * This is never modified directly - it serves as a reference point
   */
  private originalTranscription: readonly TranscriptElementLegacy[];

  /**
   * Creates a new Transcript instance
   * @param original The original transcript elements
   */
  constructor(original: TranscriptElementLegacy[]) {
    // Create a deep copy and normalize timestamps & initial index (word_index deprecated)
    const updatedTranscription = original.map((element, idx) => {
      if (Transcript.isNotRemovedTextElement(element)) {
        return {
          ...element,
          ts: Math.round(element.ts * 10000) / 10000,
          end_ts: Math.round(element.end_ts * 10000) / 10000,
          initial_index: idx,
        };
      }
      return { ...element, initial_index: idx }; // Ensure deep copy for all elements
    });
    this.originalTranscription = Object.freeze(updatedTranscription);

    // Initialize the inferred transcript helper
    // this.inferredTranscriptHelper = new InferredTranscript(
    //   this.originalTranscription,
    // );
  }

  /**
   * Gets the original transcript elements
   * @returns The original transcript elements (immutable)
   */
  getOriginalTranscription(): ReadonlyArray<TranscriptElementLegacy> {
    return this.originalTranscription;
  }

  static isNotRemovedTextElement(
    token: TranscriptElementLegacy,
  ): token is TranscriptTextToken & TimelinePositionPropsNotNull {
    return (
      token.type === 'text' &&
      token.state !== 'removed' &&
      'ts' in token &&
      'end_ts' in token
    );
  }
}
